import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import usePbPreboardingsApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import { useAuth } from '../../context/AuthContext';

const Container = styled.div``;

export default function PbPreboardingsPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite('PB') : false;

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const [drawerOpen, setDrawerOpen] = useState(false);

  const [cancelDialog, setCancelDialog] = useState({ isOpen: false, id: null, person: '' });
  const [cancelBusy, setCancelBusy] = useState(false);

  const [closeDialog, setCloseDialog] = useState({ isOpen: false, id: null, person: '' });
  const [closeBusy, setCloseBusy] = useState(false);

  const { fetching, data, pagination, columns: baseColumns, objectSchema, cancel, close, create } = usePbPreboardingsApi(queryParams);

  const handleNewButtonClick = useCallback(() => setDrawerOpen(true), []);
  const handleSave = useCallback(async (formData) => {
    const success = await create(formData);
    if (success) setDrawerOpen(false);
    return success;
  }, [create]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const handleCancelConfirm = useCallback(async () => {
    if (!cancelDialog.id) return;
    setCancelBusy(true);
    await cancel(cancelDialog.id);
    setCancelDialog({ isOpen: false, id: null, person: '' });
    setCancelBusy(false);
  }, [cancelDialog, cancel]);

  const handleCloseConfirm = useCallback(async () => {
    if (!closeDialog.id) return;
    setCloseBusy(true);
    await close(closeDialog.id);
    setCloseDialog({ isOpen: false, id: null, person: '' });
    setCloseBusy(false);
  }, [closeDialog, close]);

  // Only `active` and `extended` rows allow Cancel; only `active` allows Close
  // (closure emits E6 preboarding_completed, transitioning the LifecyclePerson
  // from `hire` to onboarding handover).
  const buildActions = useCallback((row) => {
    if (!row || !writable) return [];
    const actions = [];
    actions.push({
      type: 'details',
      label: t('preboarding.action.viewTasks'),
      onClick: () => history.push(`/preboardings/tasks?preboardingId=${row.id}`),
    });
    if (row.status === 'active') {
      actions.push({
        type: 'close',
        label: t('preboarding.action.close'),
        onClick: () => setCloseDialog({ isOpen: true, id: row.id, person: row.personFullName }),
      });
    }
    if (row.status === 'active' || row.status === 'extended') {
      actions.push({
        type: 'cancel',
        label: t('preboarding.action.cancel'),
        onClick: () => setCancelDialog({ isOpen: true, id: row.id, person: row.personFullName }),
      });
    }
    return actions;
  }, [t, writable, history]);

  const columns = useMemo(() => {
    if (!writable) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const actions = buildActions(row);
            return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
          },
        },
      },
    ];
  }, [baseColumns, data, buildActions, writable]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t('preboarding.new')}
        schema={objectSchema}
        initialValues={null}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={cancelDialog.isOpen}
        title={t('preboarding.action.cancel')}
        message={t('preboarding.cancelConfirm', { person: cancelDialog.person })}
        onConfirm={handleCancelConfirm}
        onCancel={() => setCancelDialog({ isOpen: false, id: null, person: '' })}
        loading={cancelBusy}
      />

      <ConfirmDialog
        isOpen={closeDialog.isOpen}
        title={t('preboarding.action.close')}
        message={t('preboarding.closeConfirm', { person: closeDialog.person })}
        onConfirm={handleCloseConfirm}
        onCancel={() => setCloseDialog({ isOpen: false, id: null, person: '' })}
        loading={closeBusy}
      />

      {useMemo(() => (
        <DataTable
          title={t('preboarding.pageTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={writable}
          onNewButtonClick={handleNewButtonClick}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination, writable, handleNewButtonClick,
        filterList, handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
